import * as PIXI from "pixi.js";
import React, {FC, useCallback, useEffect, useRef, useState} from "react";
import {Sprite} from "@pixi/react";
import {observer} from "mobx-react-lite";
import {GameStore} from "../model/GameStore";
import {GameState, PuckType, SpriteConfig, SpriteConfigName} from "../types";
import {GAME_LAYOUT, GAME_SPEED} from "@/entities/game/model/gameLayout";


interface GameCanvasProps {
    size: { width: number; height: number };
    game: GameStore;
}

interface SpriteWithStartX extends PIXI.Sprite {
    _startX?: number;
}

export const GameCanvas: FC<GameCanvasProps> = observer(({size, game}) => {

    const [app] = useState(() => new PIXI.Application());


    const sprites = Array.from(game.sprites.values()).filter(x => x.visible);

    const spriteRefs = useRef<Record<string, PIXI.Sprite | null>>({});

    const setSpriteRef = (name: string) => (sprite: PIXI.Sprite | null) => {
        spriteRefs.current[name] = sprite;
    };

    const startXMapRef = useRef<Partial<Record<SpriteConfigName, number>>>({});

    const playerDirectionRef = useRef(-1);
    const puckDirectionRef = useRef(-1);
    const gateDirectionRef = useRef(1);
    const goalkeeperDirectionRef = useRef(-1);

    const gateTimeRef = useRef(0);
    const goalkeeperTimeRef = useRef(0);
    const playerTimeRef = useRef(0);
    const puckTimeRef = useRef(0);

    const updateLoop = useCallback(
        (
            _: number,
            name: SpriteConfigName,
            directionRef: React.MutableRefObject<number>,
            timeRef: React.MutableRefObject<number>
        ) => {
            const sprite = spriteRefs.current[name];
            if (!sprite) return;

            const square = spriteRefs.current[SpriteConfigName.square];
            if (!square) return;

            const config = game.getSprite(name);
            if (!config || !config.visible) return;


            // puck следует за player вне режима удара
            if (name == SpriteConfigName.puck && game.gameState !== GameState.kick) {
                const player = spriteRefs.current[SpriteConfigName.player];
                if (player) {
                    sprite.x = player.x + player.width - (GAME_LAYOUT.paddingPuckForPlayerX * game.scale);
                    sprite.y = player.y - (GAME_LAYOUT.paddingPuckForPlayerY * game.scale);
                }
                return;
            }

            if (name == SpriteConfigName.goalkeeper && game.puckType == PuckType.FIRE) {
                const gate = spriteRefs.current[SpriteConfigName.gate];
                if (gate) {
                    sprite.x = gate.x + gate.width / 2;
                }
                return;
            }

            let leftEdge = 0;
            let rightEdge = 0;

            if (name == SpriteConfigName.gate) {
                leftEdge = game.offsetSquare / 2;
                rightEdge = square.width - game.offsetSquare / 2 - sprite.width;
            } else {
                leftEdge = game.offsetSquare / 2 + (config.width / 2);
                rightEdge = square.width - game.offsetSquare / 2 - (config.width / 2);
            }

            const now = performance.now() / 1000;

            if (timeRef.current === 0) {
                timeRef.current = now;
                return;
            }

            const dt = now - timeRef.current;
            timeRef.current = now;

            const speed = config.speed;
            let nextX = sprite.x + speed * dt * directionRef.current;

            if (nextX > rightEdge) {
                nextX = rightEdge - (nextX - rightEdge);
                directionRef.current = -1;
            }

            if (nextX < leftEdge) {
                nextX = leftEdge + (leftEdge - nextX);
                directionRef.current = 1;
            }

            sprite.x = nextX;

        },
        [size.width, game]
    );


    const kickStageRef = useRef(0);
    const swingLastTimeRef = useRef<number | null>(null);
    const SwingLoop = useCallback(
        (
            _: number,
            name: SpriteConfigName,
        ) => {

            const sprite = spriteRefs.current[name];
            if (!sprite) return;

            const threshold = 0.08;

            let targetRotation = 0;
            if (kickStageRef.current === 0) {
                targetRotation = Math.PI / 4;
            } else if (kickStageRef.current >= 1 && kickStageRef.current <= 2) {
                targetRotation = 0;
            } else {
                return;
            }

            const now = performance.now() / 1000;

            if (swingLastTimeRef.current === null) {
                swingLastTimeRef.current = now;
                return;
            }

            const elapsed = now - swingLastTimeRef.current;
            swingLastTimeRef.current = now;

            const currentRotation = sprite.rotation;
            const diff = targetRotation - currentRotation;

            if (Math.abs(diff) > threshold) {
                const direction = Math.sign(diff);
                const deltaRotation = direction * GAME_SPEED.rotation * elapsed;

                // не перелететь через цель
                if (Math.abs(deltaRotation) >= Math.abs(diff)) {
                    sprite.rotation = targetRotation;
                } else {
                    sprite.rotation += deltaRotation;
                }
            } else {
                sprite.rotation = targetRotation;

                if (kickStageRef.current === 0) {
                    kickStageRef.current = 1;
                } else if (kickStageRef.current === 1) {
                    kickStageRef.current = 2;
                } else if (kickStageRef.current === 2) {
                    kickStageRef.current = 3;
                    swingLastTimeRef.current = null;

                    const playerSprite = spriteRefs.current[SpriteConfigName.player];
                    if (playerSprite) {
                        game.updatePositionSprite(SpriteConfigName.player, playerSprite.x, playerSprite.y);
                    }

                }
            }
        },
        [game]
    );

    const kickLastTimeRef = useRef<number | null>(null);

    const initialPositionsRef = useRef<{
        gateY: number;
        gateRot: number;
        keeperY: number;
        keeperRot: number;
    } | null>(null);

    const fireEnteredGateZoneRef = useRef(false);

    const KickLoop = useCallback(() => {
        const gate = spriteRefs.current[SpriteConfigName.gate];
        const goalkeeper = spriteRefs.current[SpriteConfigName.goalkeeper];
        const puck = spriteRefs.current[SpriteConfigName.puck];

        const spritePuck = game.getSprite(SpriteConfigName.puck);

        if (!gate || !puck || !spritePuck) return;


        // Сохраняем исходные позиции один раз
        if (!initialPositionsRef.current) {
            initialPositionsRef.current = {
                gateY: gate.y,
                gateRot: gate.rotation,
                keeperY: goalkeeper?.y ?? 0,
                keeperRot: goalkeeper?.rotation ?? 0,
            };
        }

        if (kickStageRef.current !== 3) {
            kickLastTimeRef.current = null;
            return;
        }

        const now = performance.now() / 1000;
        if (kickLastTimeRef.current === null) {
            kickLastTimeRef.current = now;
            return;
        }

        const elapsed = now - kickLastTimeRef.current;
        kickLastTimeRef.current = now;

        // Движение шайбы
        const speed = GAME_SPEED.puck * spritePuck.speed * game.scale;
        const distance = speed * elapsed;
        puck.y -= distance;


        const puckTop = puck.y - puck.height / 2;
        const puckLeft = puck.x - puck.width / 2;
        const puckRight = puck.x + puck.width / 2;

        const gateLeft = gate.x;
        const gateRight = gate.x + gate.width;
        const gateBottom = gate.y + (gate.height - 5 * game.scale);

        const goalkeeperTop = goalkeeper ? goalkeeper.y - goalkeeper.height / 2 : 0;
        const goalkeeperBottom = goalkeeper ? goalkeeper.y + goalkeeper.height / 2 : 0;
        const goalkeeperLeft = goalkeeper ? goalkeeper.x - goalkeeper.width / 2 : 0;
        const goalkeeperRight = goalkeeper ? goalkeeper.x + goalkeeper.width / 2 : 0;

        const inGateHorizontally = puckLeft >= gateLeft && puckRight <= gateRight;

        let hit = false;
        let endDist = false;

        // Огненная шайба
        if (game.puckType === PuckType.FIRE) {

            //гол если дошли до вратаря в пределах ворот

            const puckToGoalkeeper = puckTop <= goalkeeperBottom
            const puckToGate = puckTop <= gateBottom
            if (inGateHorizontally && (goalkeeper ? puckToGoalkeeper : puckToGate)) {
                fireEnteredGateZoneRef.current = true;
                hit = true;
                game.changeState(GameState.fireAnim);
                game.setInstantHit(true);
            }

            // Промах
            if (!inGateHorizontally && puckTop <= gateBottom && !fireEnteredGateZoneRef.current) {
                game.setInstantHit(false);
                endDist = true;
            }

            // Движение вратаря
            if (goalkeeper) {
                if (inGateHorizontally && puckTop <= goalkeeperBottom && goalkeeperBottom > -500) {
                    goalkeeper.y -= distance;
                    goalkeeper.rotation += Math.PI / distance;
                }
            }

            // Движение ворот
            if (inGateHorizontally && puckTop <= gateBottom && gateBottom > -500) {
                gate.y -= distance;
                gate.rotation += Math.PI / distance;
            }


            // завершение удара
            if (puckTop <= -500 && gateBottom <= -500) {
                endDist = true;
                fireEnteredGateZoneRef.current = false;
            }
        }
        // Обычная шайба или золотая
        if (game.puckType !== PuckType.FIRE) {

            const puckReachedGateY = puckTop <= gateBottom;
            const puckReachedGoalkeeperY = puckTop <= goalkeeperBottom;

            const intersectsGoalkeeper =
                puckTop <= goalkeeperBottom &&
                puckTop >= goalkeeperTop &&
                puckLeft >= goalkeeperLeft &&
                puckRight <= goalkeeperRight;

            const intersectsGate = puckReachedGateY && inGateHorizontally;

            const missedWide = puckTop <= gate.y && !inGateHorizontally;

            if (missedWide) {
                hit = false;
                endDist = true;
                puck.y = gate.y;
            } else if (intersectsGoalkeeper && puckReachedGoalkeeperY) {
                hit = false;
                endDist = true;
                puck.y = goalkeeperBottom - 10;
            } else if (intersectsGate && !intersectsGoalkeeper) {
                hit = true;
                endDist = true;
                puck.y = gateBottom;
            }
            if (endDist) {
                game.setInstantHit(hit);
            }
        }

        // Завершение удара
        if (endDist) {

            // Восстанавливаем позиции
            if (initialPositionsRef.current) {
                gate.y = initialPositionsRef.current.gateY;
                gate.rotation = initialPositionsRef.current.gateRot;

                if (goalkeeper) {
                    goalkeeper.y = initialPositionsRef.current.keeperY;
                    goalkeeper.rotation = initialPositionsRef.current.keeperRot;
                }

            }
            gate.updateTransform();

            if (goalkeeper) {
                goalkeeper.updateTransform();
            }

            initialPositionsRef.current = null;


            if (game.gameState === GameState.kick || game.gameState === GameState.fireAnim) {
                game.setGameKick(hit);
            }


            game.changeState(GameState.kickEnd);
            kickStageRef.current = 0;
            kickLastTimeRef.current = null;
            const puckSprite = spriteRefs.current[SpriteConfigName.puck];
            if (puckSprite) {
                game.updatePositionSprite(SpriteConfigName.puck, puckSprite.x, puckSprite.y);
            }

            game.setGameAwait();
        }
    }, [game]);


    useEffect(() => {
        const ticker = PIXI.Ticker.shared;

        const tickHandler = () => {

            if (game.gameState == GameState.active || game.gameState == GameState.kick) {
                if (spriteRefs.current[SpriteConfigName.gate]) {
                    updateLoop(0, SpriteConfigName.gate, gateDirectionRef, gateTimeRef);
                }
                if (spriteRefs.current[SpriteConfigName.goalkeeper]) {
                    updateLoop(0, SpriteConfigName.goalkeeper, goalkeeperDirectionRef, goalkeeperTimeRef);
                }
            }

            if (game.gameState === GameState.active) {
                if (spriteRefs.current[SpriteConfigName.player]) {
                    updateLoop(0, SpriteConfigName.player, playerDirectionRef, playerTimeRef);
                }
                if (spriteRefs.current[SpriteConfigName.puck]) {
                    updateLoop(0, SpriteConfigName.puck, puckDirectionRef, puckTimeRef);
                }
            }

            if (game.gameState === GameState.kick) {
                if (spriteRefs.current[SpriteConfigName.player]) {
                    SwingLoop(0, SpriteConfigName.player);
                }
                KickLoop();
            }
            if (game.gameState === GameState.fireAnim) {
                KickLoop();
            }

            if (game.gameState === GameState.await || game.gameState === GameState.kickEnd || game.gameState === GameState.pause) {
                // сброс таймеров
                playerTimeRef.current = 0;
                gateTimeRef.current = 0;
                goalkeeperTimeRef.current = 0;
                puckTimeRef.current = 0;
            }

        };

        ticker.add(tickHandler);

        return () => {
            ticker.remove(tickHandler);
        };
    }, []);


    return (
        <>
            {sprites.map((sprite: SpriteConfig) => (
                <Sprite
                    key={sprite.name}
                    ref={setSpriteRef(sprite.name)}
                    image={sprite.image}
                    x={sprite.x}
                    y={sprite.y}
                    width={sprite.width}
                    height={sprite.height}
                    anchor={sprite.anchor}
                />
            ))}

        </>
    );
});
